import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { User } from '../models/User';

// --- TELEMETRY SINGLETON ---
const APPLICATION_ID = "da5ce7bb-c192-4a1b-a339-a3d6b4e5e1a9";
const APPLICATION_VERSION = "1.0.0";
const APPLICATION_REGION = "us-east-1";

let awsRum: AwsRum | null = null;
let globalTelemetryUser: User | null = null;
const eventBuffer: { eventName: string; attributes?: any }[] = [];

/**
 * Returns true if the application is running on localhost.
 */
const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
};

/**
 * Returns true if the RUM client has been initialized.
 */
export const isRumInitialized = () => awsRum !== null;

/**
 * Initializes the CloudWatch RUM client with signed credentials from Amplify.
 */
export const initializeRum = (credentials: any) => {
  if (awsRum || isLocalhost()) return;
  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: false,
      signing: true
    };
    awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    awsRum.setAwsCredentials(credentials);
    
    // Flush buffer
    while (eventBuffer.length > 0) {
      const event = eventBuffer.shift();
      if (event) recordTelemetry(event.eventName, event.attributes);
    }
  } catch (error) {
    console.error("RUM initialization failed:", error);
  }
};

/**
 * Sets the user context for all subsequent telemetry events.
 */
export const setTelemetryUser = (user: User) => {
  globalTelemetryUser = user;
  if (awsRum) {
    try {
      awsRum.addSessionAttributes({ email: user.email });
    } catch (e) { /* ignore */ }
  }
};

/**
 * Recursively flattens an object into a single-level object with snake_case keys.
 * This ensures that CloudWatch RUM can index all attributes correctly without nesting.
 */
const flattenAttributes = (attributes: any): Record<string, any> => {
  const result: Record<string, any> = {};

  const recurse = (obj: any, prefix = '') => {
    if (!obj || typeof obj !== 'object') return;

    Object.keys(obj).forEach(key => {
      // Skip userId explicitly as requested
      if (key === 'userId') return;

      const value = obj[key];
      const newKey = prefix ? `${prefix}_${key}` : key;

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        recurse(value, newKey);
      } else {
        result[newKey] = value;
      }
    });
  };

  recurse(attributes);
  return result;
};

/**
 * Records a custom event to CloudWatch RUM.
 * If RUM is not yet initialized, events are buffered and flushed upon initialization.
 * Automatically flattens nested attributes and ensures email is attached.
 */
export const recordTelemetry = (eventName: string, attributes?: any) => {
  const flatAttributes = flattenAttributes(attributes || {});
  
  if (isLocalhost()) {
    console.log('[Telemetry Local Mode]', eventName, flatAttributes);
    return;
  }

  if (awsRum) {
    awsRum.recordEvent(eventName, { 
      ...flatAttributes, 
      email: globalTelemetryUser?.email || 'unknown'
    });
  } else {
    eventBuffer.push({ eventName, attributes: flatAttributes });
  }
};

/**
 * INTERNAL USE ONLY: Resets the singleton state for unit testing.
 */
export const _resetTelemetryForTesting = () => {
  awsRum = null;
  globalTelemetryUser = null;
  eventBuffer.length = 0;
};

